<script setup lang="ts">
import BooleanIcon from '@/components/shared/BooleanIcon.vue';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import CarrierEditPopup from '@/components/maintenance/CarrierEditPopup.vue';
import { useMaintenanceDelete } from '@/composables/useMaintenanceDelete';
import { RoleGroups } from '@/constants/Roles';
import { removedMessage } from '@/helpers/notificationMessages';
import { t } from '@/i18n';
import { dataSource, useCarrierService } from '@/services/api/carrierService';
import type { ICarrierResponse } from '@/types/interfaces/MaintenanceTypes';
import Icons from '@/constants/Icons';
import DxButton from 'devextreme-vue/button';
import { DxColumn, DxDataGrid, DxItem, DxToolbar } from 'devextreme-vue/data-grid';
import { ref, shallowRef } from 'vue';

definePage({
  name: 'MaintenanceCarriers',
  meta: {
    title: 'maintenance.tabs.carriers',
    roles: RoleGroups.Maintenance
  }
});

const carriersGridRef = shallowRef<DxDataGrid>();
const carrierEditPopupRef = ref<typeof CarrierEditPopup>();
const deleteDialogRef = ref<InstanceType<typeof DeleteConfirmationDialog>>();
const api = useCarrierService();

async function confirmDelete(data: ICarrierResponse): Promise<void> {
  await deleteDialogRef?.value?.show(data.iataCode, `${data.iataCode} - ${data.name ?? t('maintenance.noName')}`);
}

const deleteCarrier = useMaintenanceDelete({
  dataSource,
  dialog: () => deleteDialogRef.value,
  remove: (carrierCode: string) => api.deleteCarrier(carrierCode),
  key: carrierCode => carrierCode,
  removedMessage: carrierCode => removedMessage(t('entities.carrier'), carrierCode.trim().toUpperCase())
});

async function updateData(data: ICarrierResponse, key?: string | null): Promise<void> {
  dataSource.upsert(data, key);
}
</script>

<template>
  <CarrierEditPopup ref="carrierEditPopupRef" @update="updateData" />
  <DeleteConfirmationDialog ref="deleteDialogRef" :entity="$t('entities.carrier')" @confirm="deleteCarrier" />

  <DxDataGrid id="carriersGrid" ref="carriersGridRef" :data-source="dataSource">
    <DxToolbar>
      <DxItem location="before" template="title-template" />
      <DxItem location="before" template="add-button-template" />
      <DxItem location="after" template="refresh-button-template" />
      <DxItem :locate-in-menu="'auto'" location="after" name="searchPanel" />
    </DxToolbar>

    <DxColumn :sort-index="0" :sort-order="'asc'" :caption="$t('labels.iataCode')" data-field="iataCode" width="150px" />
    <DxColumn :hiding-priority="0" :caption="$t('labels.icaoCode')" data-field="icaoCode" width="150px" />
    <DxColumn :hiding-priority="1" data-field="name" />
    <DxColumn cell-template="boolean-cell-template" data-field="enabled" :false-text="$t('common.no')"
        :true-text="$t('common.yes')" width="150px" />
    <DxColumn :visible="false" type="adaptive" />
    <DxColumn :alignment="'center'" :allow-filtering="false" :allow-sorting="false" caption="" cell-template="command-cell-template"
        data-field="iataCode" name="command-column" width="100px" />

    <template #title-template>
      <span class="h3 fw-semibold">{{ $t('maintenance.maintain', { entities: $t('maintenance.tabs.carriers') }) }}</span>
    </template>

    <template #add-button-template>
      <DxButton class="ms-3" type="success" @click="carrierEditPopupRef?.addCarrier()">
        <ButtonContent :icon="Icons.Plus" :text="$t('common.addEntity', { entity: $t('entities.carrier') })" />
      </DxButton>
    </template>

    <template #refresh-button-template>
      <DxButton type="primary" @click="carriersGridRef?.instance?.refresh()">
        <ButtonContent :icon="Icons.ArrowsRotate" />
      </DxButton>
    </template>

    <template #boolean-cell-template="{ data }">
      <BooleanIcon :value="data.value" />
    </template>

    <template #command-cell-template="{ data }">
      <DxButton type="primary" @click="carrierEditPopupRef?.editCarrier(data.value)">
        <ButtonContent :icon="Icons.Pencil" />
      </DxButton>
      <DxButton :visible="!data.data.airsideEnabled" type="danger" @click="confirmDelete(data.data)">
        <ButtonContent :icon="Icons.Trash" />
      </DxButton>
    </template>
  </DxDataGrid>
</template>

<style scoped>

</style>